import type { UIDebugPageLink } from '@sorrem-client/types';
import { useGetAuthSessionQuery } from '@sorrem-client/ui/store';
import type { AuthSessionResponse } from '@sorrem/shared';

/**
 * The content editors (/editors/*), in menu order. AppRoutes maps over this
 * list, so an editor added here must be given a route there.
 */
export const EDITOR_PAGES = [
    { path: '/editors/items', label: 'Item Editor', tabLabel: 'Items' },
    { path: '/editors/named-items', label: 'Named Item Designer', tabLabel: 'Named Items' },
] as const satisfies readonly UIDebugPageLink[];

/** An editor's route path. */
export type EditorPagePath = typeof EDITOR_PAGES[number]['path'];

const NO_EDITOR_PAGES: readonly UIDebugPageLink[] = [];

export interface EditorAccessState {
    /** Whether the current session holds the creator role (its own badge). False while loading. */
    isCreator: boolean;
    /** Editor pages this session may open: creators' and developers', else empty. */
    editorPages: readonly UIDebugPageLink[];
}

/**
 * Whether a session may open the editors: the Discord creator role
 * (`isCreator`), or the developer role, which covers every developer page.
 * Mirrors the server's `canUseEditors`.
 */
export function canUseEditors(session: AuthSessionResponse | undefined): boolean {
    return session !== undefined && (session.isCreator || session.isDeveloper);
}

/**
 * The current session's creator role (the `isCreator` claim from
 * GET /api/auth/session) and the editor pages it unlocks.
 */
export function useEditorAccess(): EditorAccessState {
    const { data } = useGetAuthSessionQuery();

    return {
        isCreator: data?.isCreator ?? false,
        editorPages: canUseEditors(data) ? EDITOR_PAGES : NO_EDITOR_PAGES,
    };
}
